/**
 * マニフェスト（M1 サーバ API の /api/v1/books/{slug}/manifest）の型とセッション。
 *
 * マニフェストは core が所有する。UI（目次・ページラベル）、engine（ページ寸法）、
 * source（署名 URL）が同じ 1 つの真実を参照し、署名の期限切れはここでの再取得で吸収する。
 */
import { ViewerError } from './errors';

/** 画像モードのページ画像（実ピクセル寸法）。 */
export interface PageImage {
  width: number;
  height: number;
}

/**
 * DZI 準拠タイルピラミッド（寸法閾値を超えたページにだけ付く）。
 * 格子（cols/rows）は配らない — 画像寸法とタイルサイズから同じ数式で導出する。
 */
export interface PageTiles {
  maxLevel: number;
  minLevel: number;
}

/** 画像モードの生成パラメータ（ブック共通）。 */
export interface ImagingInfo {
  tileSize: number;
  overlap: number;
  /** サーバが持っている形式（先頭が優先）。クライアントは対応する形式を選ぶ。 */
  formats: string[];
  viewEdge: number;
  maxEdge: number;
}

/** ページ配信の接頭辞と、その配下すべてで使い回す 1 本の署名。 */
export interface PageUrls {
  /** 例: https://host/api/v1/books/12/pages（末尾スラッシュなし） */
  base: string;
  /** 署名クエリ（先頭の '?' を含まない）。 */
  query: string;
  expiresAt: number;
}

/** ページ情報。width/height は PDF のポイント値。 */
export interface PageInfo {
  no: number;
  label: string;
  width: number;
  height: number;
  /** 画像モードのみ。 */
  image?: PageImage;
  /** 画像モードかつ閾値超過ページのみ（無い場合は full 1 枚で拡大する）。 */
  tiles?: PageTiles;
}

/** 目次ノード。page は 1 始まりのページ番号。 */
export interface OutlineNode {
  title: string;
  page: number;
  children?: OutlineNode[];
}

/** 塞がれたページ範囲と、その理由コード（サーバの拒否コードと同じ語彙・EP-5）。 */
export interface LockedRange {
  from: number;
  to: number;
  code: string;
}

export interface BookManifest {
  slug: string;
  title: string;
  deliveryMode: string;
  /** 綴じ方向（コンテンツの属性・取り込み時に決まる）。省略時は左綴じ。 */
  direction?: string;
  /**
   * 誌面本文の言語（コンテンツの属性・取り込み時に決まる。WCAG 3.1.2）。
   * ホストページの `lang` とも、UI の言語（`i18n.locale`）とも別物。省略時は日本語。
   */
  lang?: string;
  pageCount: number;
  pages: PageInfo[];
  outline: OutlineNode[];
  textAvailable: boolean;
  /** 署名 URL の失効時刻（unix 秒）。 */
  expiresAt: number;
  urls: {
    pdf?: string;
    /** ページ番号（文字列キー）→ テキスト層 JSON の署名 URL。 */
    text?: Record<string, string>;
    /** 書内検索 API の署名 URL（テキスト層＝索引があるブックにだけ付く）。 */
    search?: string;
    /** オーバーレイ配信 API の署名 URL（公開済みが 1 件以上あるブックにだけ付く・EP-2）。 */
    overlays?: string;
    /** ページ成果物（画像・タイル・サムネイル）の接頭辞と署名。 */
    pages?: PageUrls;
  };
  /**
   * 閲覧を許可されたページ範囲（[開始, 終了] の閉区間・1 始まり）。
   * サーバがページ範囲を制限したときだけ付く（EP-5）。無ければ全ページ許可。
   */
  allowedPages?: [number, number][];
  /**
   * 塞がれたページとその理由（EP-5・評価順）。案内文はゲート・パスワード・会員種別で
   * 変わるため、`allowedPages` だけでは正しい文言を出せない。
   */
  lockedPages?: LockedRange[];
  /** 画像モードのときだけ付く。 */
  imaging?: ImagingInfo;
  /** サーバ生成サムネイルの有無。無ければクライアント描画へフォールバックする。 */
  thumbsAvailable?: boolean;
}

/** サーバのエラー封筒。 */
interface ErrorEnvelope {
  error?: { code?: string; message?: string };
}

async function fetchManifest(url: string, signal: AbortSignal): Promise<BookManifest> {
  let response: Response;
  try {
    response = await fetch(url, { signal, credentials: 'same-origin' });
  } catch (cause) {
    if (signal.aborted) throw cause;
    throw new ViewerError('E_NETWORK', `マニフェストを取得できない: ${url}`, { cause });
  }
  if (!response.ok) {
    const envelope = (await response.json().catch(() => ({}))) as ErrorEnvelope;
    throw new ViewerError(
      envelope.error?.code ?? 'E_HTTP',
      envelope.error?.message ?? `マニフェストの取得に失敗（HTTP ${response.status}）`,
    );
  }
  return (await response.json()) as BookManifest;
}

/**
 * マニフェストのセッション。署名 URL は期限つきのため、失効前後は再取得で更新する。
 * 並行する refresh() は 1 本の要求に合流する。
 */
export class ManifestSession {
  private manifest: BookManifest | null = null;
  private pending: Promise<BookManifest> | null = null;
  private readonly controller = new AbortController();

  constructor(private readonly url: string) {}

  /** 初回取得。dispose() で進行中の取得も中断される。 */
  async open(): Promise<BookManifest> {
    this.manifest = await fetchManifest(this.url, this.controller.signal);
    return this.manifest;
  }

  /** 現在のマニフェスト（常に最新の取得結果）。 */
  get current(): BookManifest {
    if (!this.manifest) {
      throw new ViewerError('E_STATE', 'マニフェストが未取得（open() より前の参照）');
    }
    return this.manifest;
  }

  /** 署名の失効が近いか（既定は 60 秒の余裕を見る）。 */
  isExpired(marginSec = 60): boolean {
    return this.current.expiresAt - marginSec <= Date.now() / 1000;
  }

  /** 署名 URL を貼り替えるため再取得する（並行呼び出しは合流）。 */
  refresh(): Promise<BookManifest> {
    if (this.pending) return this.pending;
    this.pending = fetchManifest(this.url, this.controller.signal)
      .then((manifest) => {
        this.manifest = manifest;
        return manifest;
      })
      .finally(() => {
        this.pending = null;
      });
    return this.pending;
  }

  /** 失効していれば再取得したうえで最新を返す。 */
  async ensureFresh(): Promise<BookManifest> {
    return this.isExpired() ? this.refresh() : this.current;
  }

  dispose(): void {
    this.controller.abort();
    this.pending = null;
  }
}
